import { v4 as uuidv4 } from 'uuid';
import { getDB } from './index';

/**
 * eventsRepo — all reads + writes go through here. The api/client.js
 * interceptor routes /babies/{id}/events traffic into these methods so
 * existing call sites don't have to change.
 *
 * Every local row carries sync metadata:
 *   syncStatus: 'local' | 'syncing' | 'synced' | 'error'
 *   localUpdatedAt: ISO
 *   deletedAt: ISO | null  — tombstone for soft delete
 */

function nowIso() {
  return new Date().toISOString();
}

export function newLocalId() {
  return `local-${uuidv4()}`;
}

export async function listForBaby(babyId, filters = {}) {
  if (!babyId) return [];
  const db = getDB();

  let rows;
  if (filters.type) {
    rows = await db.events
      .where('[babyId+type]')
      .equals([babyId, filters.type])
      .toArray();
  } else {
    rows = await db.events.where('babyId').equals(babyId).toArray();
  }

  // Apply remaining filters in memory (cheap for our row counts).
  let out = rows.filter((r) => !r.deletedAt);

  // from/to may be full ISO timestamps or date-only strings (YYYY-MM-DD).
  // Date-only bounds mirror the server's GSI1SK date-bucket semantics: compare
  // against the event's UTC date prefix. A raw string compare of a date-only
  // `to` against a full ISO startTime sorts BEFORE any same-day timestamp and
  // silently drops today's events (dashboard "Today's Summary" showed 0).
  const boundKey = (ts, bound) =>
    String(bound).length === 10 ? String(ts).substring(0, 10) : String(ts);
  if (filters.from) {
    out = out.filter((r) => boundKey(r.startTime, filters.from) >= String(filters.from));
  }
  if (filters.to) {
    out = out.filter((r) => boundKey(r.startTime, filters.to) <= String(filters.to));
  }
  if (filters.subType) {
    out = out.filter((r) => (r.subType || r.data?.subType) === filters.subType);
  }

  out.sort((a, b) => String(b.startTime).localeCompare(String(a.startTime)));
  return out;
}

export async function get(eventId) {
  return getDB().events.get(eventId);
}

export async function createLocal({ userId, babyId, payload }) {
  const db = getDB();
  const eventId = newLocalId();
  const row = {
    eventId,
    userId,
    babyId,
    type: payload.type,
    subType: payload.subType,
    startTime: payload.startTime || nowIso(),
    // Store null (not undefined) so a local open sleep matches the server row
    // exactly — `endTime: null` is the canonical "fell asleep" shape.
    endTime: payload.endTime ?? null,
    data: payload.data || {},
    notes: payload.notes,
    syncStatus: 'local',
    localUpdatedAt: nowIso(),
    deletedAt: null,
  };
  // copy any other top-level fields verbatim (callers occasionally send extras)
  for (const k of Object.keys(payload)) {
    if (!(k in row)) row[k] = payload[k];
  }
  await db.events.put(row);
  return row;
}

export async function updateLocal(eventId, patch) {
  const db = getDB();
  const existing = await db.events.get(eventId);
  if (!existing) return null;
  const next = {
    ...existing,
    ...patch,
    data: { ...(existing.data || {}), ...(patch.data || {}) },
    syncStatus: 'local',
    localUpdatedAt: nowIso(),
  };
  await db.events.put(next);
  return next;
}

export async function softDelete(eventId) {
  const db = getDB();
  const existing = await db.events.get(eventId);
  if (!existing) return;
  await db.events.put({
    ...existing,
    deletedAt: nowIso(),
    syncStatus: existing.syncStatus === 'local' ? 'local' : 'local',
    localUpdatedAt: nowIso(),
  });
}

export async function hardDelete(eventId) {
  await getDB().events.delete(eventId);
}

// Key-order-independent serialisation, so two rows with the same content
// compare equal whichever way the server and Dexie happened to order them.
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

/**
 * Server gave us a row. Merge it, but never clobber a row that has pending
 * local changes — the sync engine will reconcile those on its next push.
 *
 * Resolves with what happened — 'inserted' | 'updated' | 'unchanged' |
 * 'skipped' — so a pull can tell rows that actually changed from the newest
 * page it re-reads every heartbeat. The sync engine refreshes the
 * server-computed sleep schedule only on real changes (v1.4.3: counting every
 * pulled row refetched it every five minutes).
 */
export async function upsertFromServer(serverRow) {
  if (!serverRow?.eventId) return 'skipped';
  const db = getDB();
  const existing = await db.events.get(serverRow.eventId);
  if (existing && existing.syncStatus === 'local') return 'skipped';
  const candidate = {
    ...serverRow,
    syncStatus: 'synced',
    localUpdatedAt: existing?.localUpdatedAt || nowIso(),
    deletedAt: serverRow.deletedAt || null,
  };
  if (existing && stableStringify(existing) === stableStringify(candidate)) return 'unchanged';
  await db.events.put(candidate);
  return existing ? 'updated' : 'inserted';
}

export async function markSyncing(eventId) {
  await getDB().events.update(eventId, { syncStatus: 'syncing' });
}

export async function markSynced(eventId) {
  await getDB().events.update(eventId, { syncStatus: 'synced' });
}

export async function clearForUser(userId) {
  await getDB().events.where('userId').equals(userId).delete();
}
